import React, {
  ReactNode,
  Dispatch,
  SetStateAction,
  MouseEvent,
  useState,
} from "react";

import {
  Paper,
  useTheme,
  IconButton,
  Divider,
  Tooltip,
  Stack,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import StraightenIcon from "@mui/icons-material/Straighten";
import SubtitlesOffOutlinedIcon from "@mui/icons-material/SubtitlesOffOutlined";
import SubtitlesOutlinedIcon from "@mui/icons-material/SubtitlesOutlined";
import {
  TuneOutlined,
  LinkOffOutlined,
  TrendingDown,
} from "@mui/icons-material";

import { useReactFlow } from "reactflow";

import ConfiguracaoMapModal from "../configMapModal";
import OnusDownPanel from "../onusDownPanel";
import { usePersistentOpen } from "../draggableModal";

interface ToolBarMapsPublicProps {
  setFullScreem: (value: boolean) => void;
  fullScreem?: boolean;

  setShowLabel: (value: boolean) => void;
  showLabel?: boolean;

  setShowLabelEnlace?: (value: boolean) => void;
  showLabelEnlace?: boolean;

  children?: ReactNode;

  tipo?: number;

  setEasyMapsConfig?: Dispatch<SetStateAction<any>>;
  easyMapsConfig?: any;

  setRulerPoint?: (value: any[]) => void;
  setShowRuler?: (value: boolean) => void;
  showRuler?: boolean;

  collapse?: any;

  isFtth?: boolean;
  config?: any;
}

interface IconButtonCustomProps {
  title: string;
  iconOn: React.ElementType;
  iconOff: React.ElementType;
  variable: boolean;
  onclick?: (value: any) => void;
  sendEvent?: boolean;
}

interface InputZoomGeoProps {
  zoom: (direction: number) => void;
}

const ToolBarMapsPublic = ({
  setFullScreem,
  fullScreem = false,
  setShowLabel,
  showLabel = true,
  setShowLabelEnlace,
  showLabelEnlace = true,
  children,
  tipo = 0,
  setEasyMapsConfig = () => {},
  easyMapsConfig,
  setRulerPoint,
  setShowRuler,
  showRuler = false,
  collapse,
  isFtth = false,
  config,
}: ToolBarMapsPublicProps) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // usePersistentOpen é JS: sem o cast o TS infere array, não tupla
  const [openOnusDown, setOpenOnusDown] = usePersistentOpen(
    "easyMapsOnusDownPanelOpen",
  ) as [boolean, Dispatch<SetStateAction<boolean>>];

  const styles = {
    switch: {
      width: "100%",
      ml: "0px",
      mr: "0px",
    },
    icon: {
      color: (theme.palette as any).color?.icons,
    },
    button: {
      borderRadius: "5px",
      padding: "5px",
    },
    card: {
      padding: "5px",
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      top: "15px",
      ml: { xs: "10px", md: "15px" },
      transition: "margin-left 0.3s ease",
      alignItems: "center",
      gap: 0.5,
      justifyContent: "center",
      backgroundColor: theme.palette.background.default,
      zIndex: 10,
    },
    paper: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    path: string,
    name: string,
  ) => {
    setEasyMapsConfig((data: any) => {
      if (data?.[path]) {
        data[path][name] = event.target.checked;
      } else {
        data = {};
        data[path] = { [name]: event.target.checked };
      }

      return { ...data };
    });
  };

  const handleChangeCto = (value: any, path: string, name: string) => {
    setEasyMapsConfig((data: any) => {
      if (data?.[path]) {
        data[path][name] = value;
      } else {
        if (!data) data = {};
        data[path] = { [name]: value };
      }

      return { ...data };
    });
  };

  return (
    <>
      <Paper elevation={0} sx={styles.card}>
        {children}

        <IconButtonCustom
          title={fullScreem ? "Sair da Tela Cheia" : "Tela Cheia"}
          iconOn={FullscreenIcon}
          iconOff={FullscreenExitIcon}
          variable={fullScreem}
          onclick={setFullScreem}
        />

        <IconButtonCustom
          title={showLabel ? "Ocultar Detalhes" : "Mostrar detalhes"}
          iconOn={SubtitlesOutlinedIcon}
          iconOff={SubtitlesOffOutlinedIcon}
          variable={showLabel}
          onclick={setShowLabel}
        />

        {setShowRuler && (
          <IconButtonCustom
            title={showRuler ? "Desativar Régua" : "Ativar Régua"}
            iconOn={StraightenIcon}
            iconOff={StraightenIcon}
            variable={showRuler}
            onclick={() => {
              setShowRuler(!showRuler);
              setRulerPoint?.([]);
            }}
          />
        )}

        {isFtth && (
          <IconButtonCustom
            title="ONUs down"
            iconOn={TrendingDown}
            iconOff={TrendingDown}
            variable={openOnusDown}
            onclick={() => setOpenOnusDown((prev) => !prev)}
          />
        )}

        <IconButtonCustom
          title="Ajustes"
          iconOn={TuneOutlined}
          iconOff={TuneOutlined}
          variable={Boolean(anchorEl)}
          sendEvent
          onclick={handleClick}
        />
      </Paper>

      <ConfiguracaoMapModal open={Boolean(anchorEl)} setOpen={handleClose} />
      {isFtth && (
        <OnusDownPanel
          open={openOnusDown}
          onClose={() => setOpenOnusDown(false)}
        />
      )}
    </>
  );
};

const IconButtonCustom = ({
  title,
  iconOn: IconOn,
  iconOff: IconOff,
  variable,
  onclick = () => {},
  sendEvent = false,
}: IconButtonCustomProps) => {
  const theme = useTheme();

  const styles = {
    button: {
      borderRadius: "5px",
      padding: "5px",
      boxShadow: "0px 0px 5px #00000015",
      backgroundColor: (theme.palette as any).background.paperSelectedMaps,
      "&:hover": {
        backgroundColor: (theme.palette as any).background.hover,
      },
    },
  };

  return (
    <Tooltip title={title} placement="right">
      <IconButton
        aria-label="custom-icon-button"
        sx={{
          ...styles.button,
          ...(variable
            ? {
                color: theme.palette.primary.main,
              }
            : {}),
        }}
        onClick={(event) => onclick(sendEvent ? event : !variable)}
      >
        <IconOn />
      </IconButton>
    </Tooltip>
  );
};

const InputZoomTop = () => {
  const { zoomIn, zoomOut } = useReactFlow();

  return (
    <Stack>
      <IconButtonCustom
        title="Zoom In"
        iconOn={AddIcon}
        iconOff={AddIcon}
        variable={false}
        onclick={() => zoomIn()}
      />

      <Divider sx={{ width: "80%", ml: "10%" }} />

      <IconButtonCustom
        title="Zoom Out"
        iconOn={RemoveIcon}
        iconOff={RemoveIcon}
        variable={false}
        onclick={() => zoomOut()}
      />
    </Stack>
  );
};

const InputZoomGeo = ({ zoom }: InputZoomGeoProps) => {
  return (
    <Stack>
      <IconButtonCustom
        title="Zoom In"
        iconOn={AddIcon}
        iconOff={AddIcon}
        variable={false}
        onclick={() => zoom(1)}
      />

      <Divider sx={{ width: "80%", ml: "10%" }} />

      <IconButtonCustom
        title="Zoom Out"
        iconOn={RemoveIcon}
        iconOff={RemoveIcon}
        variable={false}
        onclick={() => zoom(-1)}
      />
    </Stack>
  );
};

const ToolBarMapsPublicWithSubs =
  ToolBarMapsPublic as typeof ToolBarMapsPublic & {
    InputZoomGeo: typeof InputZoomGeo;
    InputZoomTop: typeof InputZoomTop;
  };

ToolBarMapsPublicWithSubs.InputZoomGeo = InputZoomGeo;
ToolBarMapsPublicWithSubs.InputZoomTop = InputZoomTop;

export default ToolBarMapsPublicWithSubs;
